// The targets lab, `?scene=targets` (gates B3 and B6): the Wanderer and enemy dummies on a small ground with a bank,
// in the low-poly look, under the @rpg/camera rig, with the spike's targeting (./targeting.ts) and the impact
// director (../feedback). It gives the handle `__rpg.targets`:
//   - the 20 scripted layouts of the targeting spec (./layouts.ts): `layout(i)` places them, `choose()` asks the
//     soft chooser with the scene's real line of sight (Rapier ray casts), `runLayouts()` does all 20;
//   - lock-on: the state, `kill`, `place`, `framed()` (whether hero and target are both on screen);
//   - a test emitter of the combat events the combat stream emits for real (`combat:hit`, `combat:move`,
//     `combat:telegraph`, `combat:defeated`), queued to a sim time and emitted on the kernel game's bus inside a sim
//     step, as combat will; and the impact director's report (`feedback()`).
// The lab's sim state (the dummies, the lock) lives here, beside the A1 pen that steps offstage; at the merge it is
// the combat stream's. Weapons: the hero's sword swings along a scripted arc during a move's active window (the bodies
// stream poses the real arms); trails read that arc.
import * as THREE from "three";
import { createCollisionWorld, type CollisionWorld, type Rapier } from "@rpg/collision";
import { SteppedTarget } from "@rpg/camera";
import type { EventBus } from "@rpg/kernel";
import { buildFigure, createMotion, type Attachment, type Figure, type FigureCard, type Locomotion, type Motion } from "@rpg/figure";
import { createLowPolyLook, type LowPolyLook, type LowPolySceneLook, type LowPolyTier } from "@rpg/look";
import { facet, merge, seeded } from "@rpg/meshkit";
import type { TargetCandidate, TargetFrom } from "@rpg/combat";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { ContentTables } from "../content";
import type { CombatHit, CombatTelegraph, GameEvents, HitKind, Vec3 } from "../core";
import { mountCamera, type LookSource, type SpikeCamera } from "../camera";
import type { SpikeInput } from "../input";
import { mountFeedback, reduceMotionOf, type Feedback } from "../feedback";
import { LAYOUTS, type Layout } from "./layouts";
import { mountTargeting, type Targeting } from "./targeting";

/** The hero's entity id; dummies are 2 to 7. */
export const HERO = 1;
const DUMMY_IDS = [2, 3, 4, 5, 6, 7] as const;
const DUMMY_CARDS = ["shade", "brute", "wisp"] as const;

/** The lab's ground: flat round the middle (the layouts), a bank rising to the east (+X) for the telegraphs to drape on. */
export function targetsHeight(x: number, z: number): number {
  const bank = x > 5 ? Math.min(2.2, (x - 5) * 0.32) : 0;
  const ripple = 0.08 * Math.sin(x * 0.7 + 1.3) * Math.cos(z * 0.6);
  return bank + (x > 4 ? ripple * Math.min(1, x - 4) : 0) + (Math.hypot(x, z) > 15 ? (Math.hypot(x, z) - 15) * 0.3 : 0);
}

/** One placed body. */
interface Body {
  id: number;
  fig: Figure;
  motion: Motion;
  card: string;
  x: number;
  z: number;
  facing: number;
  alive: boolean;
  present: boolean;
  priority: number;
}

/** A scripted swing: the weapon's arc during a move's active window (sim seconds). */
interface Swing {
  start: number;
  seconds: number;
}

/** What the lab needs from main.ts. */
export interface TargetsLabOptions {
  runtime: Runtime;
  renderer: THREE.WebGLRenderer;
  rapier: Rapier;
  content: SpikeContent;
  tier: string;
  /** The kernel game's bus (the sim host's). */
  events: EventBus<GameEvents>;
  input: SpikeInput;
  /** A setting's value now. */
  setting: (id: string) => unknown;
  /** The look input (the input's `look` action). */
  look: LookSource;
}

/** The mounted lab. */
export interface TargetsLab {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  render(): void;
  state(): { layout: string | null; locked: number | null; mode: string; telegraphs: number; trails: number };
  handle: ReturnType<typeof labHandle>;
  targeting: Targeting;
  feedback: Feedback;
}

/** Mount the lab. */
export function mountTargetsLab(o: TargetsLabOptions): TargetsLab {
  const { runtime, renderer } = o;
  const registry = o.content.registry;
  const sceneLook = registry.get("looks", "valley-day") as unknown as LowPolySceneLook | undefined;
  const tierRow = registry.get("tiers", o.tier === "low" ? "low" : "high") as unknown as LowPolyTier | undefined;
  if (!sceneLook || !tierRow) throw new Error("targets lab: no valley-day look or tier row");
  const scene = new THREE.Scene();
  scene.name = "targets-lab";
  const camera = new THREE.PerspectiveCamera(55, innerWidth / innerHeight, 0.1, 400);
  const look = createLowPolyLook({ renderer, scene, camera, look: sceneLook, tier: tierRow, shadowExtent: 14 });

  // ── the ground and the walls ──
  const world = createCollisionWorld(o.rapier, { dt: runtime.clock.dt, layers: { ground: [], solid: [] } });
  const ground = buildGround(look, world);
  scene.add(ground);
  look.track(ground);
  world.step();
  const probe = (x: number, z: number, fromY: number): number | null => world.castRay({ x, y: fromY, z }, { x: 0, y: -1, z: 0 }, 40, { layers: ["ground"] })?.point.y ?? null;
  const heightAt = (x: number, z: number): number => probe(x, z, 30) ?? targetsHeight(x, z);
  const wallMat = look.surface;
  const walls: THREE.Mesh[] = [];
  const wallIds: string[] = [];

  // ── the cast ──
  const attachments = new Map<string, Attachment>(registry.rows("attachments").map((a: ContentTables["attachments"]) => [a.id, a as unknown as Attachment]));
  const cardOf = (id: string): FigureCard => {
    const row = registry.get("cards", id);
    if (!row) throw new Error(`targets lab: no card "${id}"`);
    return row as unknown as FigureCard;
  };
  const makeBody = (id: number, card: string): Body => {
    const c = cardOf(card);
    const fig = buildFigure(c, { material: look.surface, colour: look.palette });
    if (card === "wanderer") fig.attach(attachments.get("sword") ?? null);
    const motion = createMotion(fig, c.motion!, probe);
    fig.root.name = `targets:${card}:${id}`;
    scene.add(fig.root);
    look.track(fig.root, { blob: (registry.get("cards", card) as { blob?: number } | undefined)?.blob });
    return { id, fig, motion, card, x: 0, z: 0, facing: 0, alive: true, present: true, priority: 0 };
  };
  const hero = makeBody(HERO, "wanderer");
  const dummies = new Map<number, Body>(DUMMY_IDS.map((id, i) => [id, makeBody(id, DUMMY_CARDS[i % 3]!)]));
  const bodies = (): Body[] => [hero, ...dummies.values()];
  let stick: [number, number] | null = null;
  let layoutName: string | null = null;

  const placeBody = (b: Body): void => {
    b.fig.root.visible = b.present;
    b.fig.root.position.set(b.x, heightAt(b.x, b.z), b.z);
    b.fig.root.rotation.set(b.alive ? 0 : -Math.PI / 2, b.facing, 0, "YXZ");
    if (!b.alive) b.fig.root.position.y += 0.25;
    b.motion.reset();
  };
  const faceHero = (b: Body): void => {
    b.facing = Math.atan2(hero.x - b.x, hero.z - b.z);
  };

  // ── the camera on the hero, with the scene's collision; look input through the targeting (flicks while locked) ──
  const target = new SteppedTarget({ position: { x: 0, y: 0, z: 0 }, velocity: { x: 0, y: 0, z: 0 }, facing: 0, grounded: true });
  let wrapped: LookSource = o.look;
  const cam: SpikeCamera = mountCamera({
    runtime,
    camera,
    target,
    content: o.content,
    collision: { world, layers: ["ground", "solid"] },
    look: (dt) => wrapped(dt),
    setting: o.setting,
  });
  const heroState = () => ({ position: { x: hero.x, y: heightAt(hero.x, hero.z), z: hero.z }, velocity: { x: 0, y: 0, z: 0 }, facing: hero.facing, grounded: true });
  runtime.on("sim", () => target.push(heroState()), { order: 1e8, name: "targets: camera target" });

  // ── targeting ──
  const pos = (b: Body): [number, number, number] => [b.x, heightAt(b.x, b.z), b.z];
  const candidates = (): TargetCandidate[] => [...dummies.values()].filter((b) => b.present).map((b) => ({ id: b.id, pos: pos(b), alive: b.alive, priority: b.priority }));
  const from = (): TargetFrom => ({ pos: pos(hero), facing: hero.facing, input: stick });
  const lineOfSight = (a: readonly number[], b: readonly number[]): boolean => {
    const d = { x: b[0]! - a[0]!, y: b[1]! - a[1]!, z: b[2]! - a[2]! };
    const len = Math.hypot(d.x, d.y, d.z);
    if (len < 1e-6) return true;
    return world.castRay({ x: a[0]!, y: a[1]!, z: a[2]! }, d, len - 0.05, { layers: ["ground", "solid"] }) === null;
  };
  const targeting = mountTargeting({ runtime, actions: o.input.input.actions, content: o.content, setting: o.setting, from, candidates, lineOfSight, camera: cam });
  wrapped = targeting.look(o.look);
  // the lab is a fight: the combat context (its actions: lock) is on while it runs
  o.input.input.contexts.push("combat");

  // ── the impact director ──
  const swings = new Map<number, Swing>();
  const weaponOf = (owner: number): { base: Vec3; tip: Vec3 } | null => {
    const b = owner === HERO ? hero : dummies.get(owner);
    const s = swings.get(owner);
    if (!b || !s) return null;
    const t = Math.min(1, Math.max(0, (runtime.clock.time - s.start) / s.seconds));
    // a flat sweep from the right shoulder across the front to the left, a little downward
    const a = b.facing + (1.25 - 2.5 * t) * -1;
    const y = heightAt(b.x, b.z) + 1.25 - 0.35 * t;
    const base: Vec3 = [b.x + Math.sin(a) * 0.35, y, b.z + Math.cos(a) * 0.35];
    const tip: Vec3 = [b.x + Math.sin(a) * 1.35, y - 0.1, b.z + Math.cos(a) * 1.35];
    return { base, tip };
  };
  const feedback = mountFeedback({
    runtime,
    events: o.events,
    scene,
    content: o.content,
    rig: cam.rig,
    weaponOf,
    heightAt,
    motion: () => ({ shake: Number(o.setting("camera.shake") ?? 1), reduceMotion: reduceMotionOf(o.setting("accessibility.reduceMotion")) }),
  });
  for (const b of bodies()) feedback.track(b.id, b.fig.root);

  // ── the test emitter: events queued to a sim time, emitted on the kernel's bus inside a sim step ──
  type Queued = { at: number; emit: () => void };
  const queue: Queued[] = [];
  const emitted: Record<string, number> = {};
  const at = (seconds: number, emit: () => void): void => {
    queue.push({ at: runtime.clock.time + seconds, emit });
    queue.sort((a, b) => a.at - b.at);
  };
  runtime.on(
    "sim",
    (c) => {
      while (queue.length && queue[0]!.at <= c.time + 1e-9) queue.shift()!.emit();
    },
    { order: -5, name: "targets: test emitter" },
  );
  const emit = <K extends keyof GameEvents>(name: K, payload: GameEvents[K]): void => {
    emitted[name] = (emitted[name] ?? 0) + 1;
    o.events.emit(name, payload);
  };

  // ── posing, the camera's sun focus ──
  const still: Locomotion = { speed: 0, velocity: { x: 0, y: 0, z: 0 }, grounded: true, jump: false, glide: false };
  runtime.on(
    "late",
    (c) => {
      const n = Math.min(30, Math.max(1, Math.ceil(c.realDt * 60 - 1e-6)));
      for (let i = 0; i < n; i++)
        for (const b of bodies()) {
          if (!b.present) continue;
          b.motion.update(c.realDt / n, { ...still, acting: b === hero, lookAt: b === hero ? lookAtFor() : null });
          if (!b.alive) b.fig.root.updateMatrixWorld(true);
        }
      look.focus(new THREE.Vector3(hero.x, heightAt(hero.x, hero.z), hero.z));
    },
    { order: 5, name: "targets: pose" },
  );
  const lookAtFor = (): { x: number; y: number; z: number } | null => {
    const id = targeting.locked();
    const t = id === null ? null : dummies.get(id);
    return t ? { x: t.x, y: heightAt(t.x, t.z) + 1.2, z: t.z } : null;
  };

  // ── layouts ──
  const setWalls = (ws: Layout["walls"]): void => {
    for (const id of wallIds.splice(0)) world.remove(id);
    for (const m of walls.splice(0)) {
      m.removeFromParent();
      m.geometry.dispose();
    }
    (ws ?? []).forEach((w, i) => {
      const y = heightAt(w.x, w.z);
      const id = `targets:wall${i}`;
      world.add({ id, kind: "fixed", position: { x: w.x, y: y + 1.2, z: w.z }, colliders: [{ layer: "solid", tag: "stone", shape: { type: "cuboid", halfExtents: { x: w.w / 2, y: 1.2, z: w.d / 2 } } }] });
      wallIds.push(id);
      const g = facet(new THREE.BoxGeometry(w.w, 2.4, w.d).translate(w.x, y + 1.2, w.z), { colour: look.palette("stone.a"), jitter: 0, tint: 0.05, rng: seeded(7 + i) });
      const m = new THREE.Mesh(g, wallMat);
      m.castShadow = true;
      m.receiveShadow = true;
      scene.add(m);
      walls.push(m);
    });
    world.step();
  };
  const applyLayout = (l: Layout): void => {
    layoutName = l.name;
    hero.x = l.hero.x;
    hero.z = l.hero.z;
    hero.facing = l.hero.facing;
    stick = l.stick ?? null;
    for (const b of dummies.values()) {
      const d = l.dummies.find((x) => x.id === b.id);
      b.present = !!d;
      b.x = d?.x ?? 0;
      b.z = d?.z ?? -30;
      b.alive = d?.alive ?? true;
      b.priority = d?.priority ?? 0;
      faceHero(b);
    }
    setWalls(l.walls);
    for (const b of bodies()) placeBody(b);
    target.teleport(heroState());
    cam.rig.yaw = hero.facing;
    cam.rig.snap();
  };

  // a starting layout: the crowd, so the lab boots onto something to look at
  applyLayout(LAYOUTS[LAYOUTS.length - 1]!);
  // compile every program the lab draws before the boot's warm-up (the compile watchdog arms after it)
  const unwarm = feedback.warm([hero.x, heightAt(hero.x, hero.z), hero.z + 2]);
  look.warm();
  unwarm();

  const lab: TargetsLab = {
    scene,
    camera,
    render: () => look.render(),
    state: () => ({ layout: layoutName, locked: targeting.locked(), mode: targeting.mode(), telegraphs: feedback.telegraphs.state().length, trails: feedback.trails.state().length }),
    targeting,
    feedback,
    handle: labHandle({
      runtime,
      camera,
      cam,
      hero,
      dummies,
      heightAt,
      targeting,
      feedback,
      applyLayout,
      placeBody,
      faceHero,
      emit,
      at,
      swings,
      emitted,
      queue,
      setStick: (s) => (stick = s),
      world,
    }),
  };
  addEventListener("resize", () => {
    camera.aspect = innerWidth / innerHeight;
    camera.updateProjectionMatrix();
    look.resize(innerWidth, innerHeight);
  });
  return lab;
}

/** A point on screen (normalised device coordinates, -1..1) and whether it is inside the view. */
function onScreen(camera: THREE.PerspectiveCamera, p: THREE.Vector3): { x: number; y: number; inView: boolean } {
  const v = p.clone().project(camera);
  const behind = p.clone().applyMatrix4(camera.matrixWorldInverse).z > 0;
  return { x: v.x, y: v.y, inView: !behind && Math.abs(v.x) <= 1 && Math.abs(v.y) <= 1 };
}

const unit = (x: number, y: number, z: number): Vec3 => {
  const l = Math.hypot(x, y, z) || 1;
  return [x / l, y / l, z / l];
};

/** The handle's `targets` key. */
function labHandle(d: {
  runtime: Runtime;
  camera: THREE.PerspectiveCamera;
  cam: SpikeCamera;
  hero: Body;
  dummies: Map<number, Body>;
  heightAt: (x: number, z: number) => number;
  targeting: Targeting;
  feedback: Feedback;
  applyLayout: (l: Layout) => void;
  placeBody: (b: Body) => void;
  faceHero: (b: Body) => void;
  emit: <K extends keyof GameEvents>(name: K, payload: GameEvents[K]) => void;
  at: (seconds: number, emit: () => void) => void;
  swings: Map<number, Swing>;
  emitted: Record<string, number>;
  queue: unknown[];
  setStick: (s: [number, number] | null) => void;
  world: CollisionWorld;
}) {
  const body = (id: number): Body => {
    const b = id === HERO ? d.hero : d.dummies.get(id);
    if (!b) throw new Error(`targets: no body ${id}`);
    return b;
  };
  const chest = (b: Body): Vec3 => [b.x, d.heightAt(b.x, b.z) + 1.1, b.z];
  const firstDummy = (): number => [...d.dummies.values()].find((b) => b.present && b.alive)?.id ?? 2;
  return {
    /** How many scripted layouts there are, and their names. */
    layouts: () => LAYOUTS.map((l) => ({ name: l.name, expect: l.expect, facingDecides: !!l.facingDecides })),
    /** Place layout `i`; returns its name and the target it expects. */
    layout: (i: number) => {
      const l = LAYOUTS[i];
      if (!l) throw new Error(`targets: no layout ${i}`);
      d.applyLayout(l);
      return { name: l.name, expect: l.expect };
    },
    /** The soft chooser's pick now (the scene's real line of sight). */
    choose: () => d.targeting.soft(),
    /** Every layout in turn: the pick against the expected target. */
    runLayouts: () =>
      LAYOUTS.map((l) => {
        d.applyLayout(l);
        const got = d.targeting.soft();
        return { name: l.name, expect: l.expect, got, ok: got === l.expect, facingDecides: !!l.facingDecides };
      }),
    /** Steer the hero's stick (a world direction `[x, z]`), or null. */
    stick: (s: [number, number] | null) => d.setStick(s),
    /** Targeting now: mode, the lock, the camera's profile and focus, the changes so far, flicks. */
    lock: () => ({
      ...d.targeting.state(),
      current: d.targeting.current(),
      cameraProfile: d.cam.rig.profiles.state().active,
      changes: [...d.targeting.changes()],
      flicks: d.targeting.flicks(),
    }),
    /** Hero and the locked target on screen (both must be in view for the lock-on framing). */
    framed: () => {
      const id = d.targeting.locked();
      const t = id === null ? null : d.dummies.get(id);
      const h = chest(d.hero);
      return {
        hero: onScreen(d.camera, new THREE.Vector3(...h)),
        target: t ? onScreen(d.camera, new THREE.Vector3(...chest(t))) : null,
        yaw: d.cam.rig.yaw,
      };
    },
    /** Defeat a dummy: it falls, and `combat:defeated` goes out (the finisher cue). */
    kill: (id: number) => {
      const b = body(id);
      b.alive = false;
      d.placeBody(b);
      const dir = unit(b.x - d.hero.x, 0, b.z - d.hero.z);
      d.at(0, () => d.emit("combat:defeated", { entity: id, by: HERO, point: chest(b), dir, severity: 1 }));
    },
    /** Move a dummy (e.g. out past the drop range). */
    place: (id: number, x: number, z: number) => {
      const b = body(id);
      b.x = x;
      b.z = z;
      b.present = true;
      d.faceHero(b);
      d.placeBody(b);
    },
    /** Queue a `combat:hit` (defaults: the hero hits the first live dummy, a clean hit, severity by kind). */
    hit: (o: { kind: HitKind; attacker?: number; target?: number; severity?: number; outcome?: CombatHit["outcome"]; unblockable?: boolean; delay?: number }) => {
      const attacker = o.attacker ?? HERO;
      const target = o.target ?? (attacker === HERO ? firstDummy() : HERO);
      const a = body(attacker);
      const t = body(target);
      const dir = unit(t.x - a.x, 0, t.z - a.z);
      const severity = o.severity ?? { light: 0.35, heavy: 0.75, skill: 0.6, big: 1, chip: 0.2, parry: 0.6, guardBreak: 0.9 }[o.kind];
      const point = chest(t);
      const hit: CombatHit = { attacker, target, move: `test.${o.kind}`, kind: o.kind, severity, damage: Math.round(severity * 20), point: [point[0] - dir[0] * 0.3, point[1], point[2] - dir[2] * 0.3], dir, outcome: o.outcome ?? "hit", unblockable: o.unblockable ?? false, crit: false };
      d.at(o.delay ?? 0, () => d.emit("combat:hit", hit));
      return hit;
    },
    /** Queue a move's phases: wind-up now, active after `windup` s for `active` s (the weapon swings, a trail), then
     *  recovery and the end. */
    swing: (o: { entity?: number; windup?: number; active?: number; recovery?: number; weapon?: string | null } = {}) => {
      const entity = o.entity ?? HERO;
      const w = o.windup ?? 0.1;
      const act = o.active ?? 0.12;
      const rec = o.recovery ?? 0.2;
      const weapon = o.weapon === undefined ? "sword" : o.weapon;
      const move = "test.swing";
      d.at(0, () => d.emit("combat:move", { entity, move, phase: "windup", weapon }));
      d.at(w, () => {
        d.swings.set(entity, { start: d.runtime.clock.time, seconds: act });
        d.emit("combat:move", { entity, move, phase: "active", weapon });
      });
      d.at(w + act, () => d.emit("combat:move", { entity, move, phase: "recovery", weapon }));
      d.at(w + act + rec, () => {
        d.swings.delete(entity);
        d.emit("combat:move", { entity, move, phase: "end", weapon });
      });
    },
    /** Queue a `combat:telegraph` from a dummy (default: the first live one, facing the hero). */
    telegraph: (o: { shape: CombatTelegraph["shape"]; attacker?: number; lead?: number; unblockable?: boolean; facing?: number; delay?: number }) => {
      const a = body(o.attacker ?? firstDummy());
      const facing = o.facing ?? Math.atan2(d.hero.x - a.x, d.hero.z - a.z);
      const t: CombatTelegraph = { attacker: a.id, move: "test.telegraph", shape: o.shape, origin: [a.x, d.heightAt(a.x, a.z), a.z], facing, lead: o.lead ?? 0.8, unblockable: o.unblockable ?? false, style: o.unblockable ? "unblockable" : "normal" };
      d.at(o.delay ?? 0, () => d.emit("combat:telegraph", t));
      return t;
    },
    /** Set a body's place and facing outright (shots). */
    pose: (id: number, x: number, z: number, facing: number) => {
      const b = body(id);
      b.x = x;
      b.z = z;
      b.facing = facing;
      b.present = true;
      d.placeBody(b);
    },
    /** Point the camera: yaw, and the pitch offset from the profile's (shots). */
    view: (yaw: number, pitch = 0) => {
      d.cam.rig.yaw = yaw;
      d.cam.rig.restore({ ...d.cam.rig.snapshot(), yaw, pitchOffset: pitch });
    },
    /** Events the emitter has sent, by name, and how many wait in its queue. */
    emitted: () => ({ ...d.emitted, queued: d.queue.length }),
    /** The impact director's report: counts per kind and channel, flashes, telegraphs, trails, particles, sounds, the
     *  camera's shake offset. */
    feedback: () => d.feedback.report(),
    /** Zero the director's counts. */
    resetCounts: () => d.feedback.router.resetCounts(),
    /** The ground's height under a telegraph vertex by a ray cast (independent of the telegraph's own height function):
     *  the largest gap, metres, over a live telegraph's vertices. */
    telegraphGap: (id: number) => {
      const mesh = d.feedback.telegraphs.mesh(id);
      if (!mesh) return null;
      const p = mesh.geometry.getAttribute("position");
      let gap = 0;
      let lo = Infinity;
      let hi = -Infinity;
      for (let i = 0; i < p.count; i += 3) {
        const x = p.getX(i);
        const z = p.getZ(i);
        const g = d.world.castRay({ x, y: 30, z }, { x: 0, y: -1, z: 0 }, 60, { layers: ["ground"] })?.point.y;
        if (g === undefined) continue;
        gap = Math.max(gap, Math.abs(p.getY(i) - g));
        lo = Math.min(lo, p.getY(i));
        hi = Math.max(hi, p.getY(i));
      }
      return { gap, relief: hi - lo };
    },
    /** The clock: sim time, hit-stop left (real seconds), steps. */
    clock: () => ({ time: d.runtime.clock.time, hitStop: d.runtime.clock.hitStopRemaining, step: d.runtime.clock.step }),
    /** The camera rig's frame (position, the shake's offset, profile). */
    camera: () => {
      const f = d.cam.rig.frame();
      return { position: f.position, shake: f.shake, profile: d.cam.rig.profiles.state().active, actual: { x: d.camera.position.x, y: d.camera.position.y, z: d.camera.position.z } };
    },
  };
}

/** The lab's ground: palette facets over the height function, a few stones; the same triangles as its collider. */
function buildGround(look: LowPolyLook, world: CollisionWorld): THREE.Mesh {
  const rng = seeded(9090);
  const pick = <T,>(a: readonly T[]): T => a[Math.floor(rng() * a.length)]!;
  const N = 44;
  const S = 44;
  const pos: number[] = [];
  const col: number[] = [];
  const v = (i: number, j: number): [number, number, number] => {
    const x = -S / 2 + (i / N) * S;
    const z = -S / 2 + (j / N) * S;
    return [x, targetsHeight(x, z), z];
  };
  const grass = ["grass.a", "grass.b", "grass.c", "grass.d"].map((k) => look.palette(k));
  const path = ["path.a", "path.b"].map((k) => look.palette(k));
  for (let j = 0; j < N; j++)
    for (let i = 0; i < N; i++) {
      const a = v(i, j);
      const b = v(i + 1, j);
      const c = v(i, j + 1);
      const dd = v(i + 1, j + 1);
      for (const tri of [[a, c, b], [b, c, dd]] as [number, number, number][][]) {
        const cx = (tri[0]![0] + tri[1]![0] + tri[2]![0]) / 3;
        const cz = (tri[0]![2] + tri[1]![2] + tri[2]![2]) / 3;
        const k = Math.hypot(cx, cz) < 2.2 ? pick(path) : pick(grass);
        for (const p of tri) {
          pos.push(...p);
          col.push(k.r, k.g, k.b);
        }
      }
    }
  const terrain = new THREE.BufferGeometry();
  terrain.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  terrain.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  terrain.setAttribute("glow", new THREE.Float32BufferAttribute(new Float32Array(pos.length / 3), 1));
  terrain.computeVertexNormals();
  world.add({ id: "targets:ground", kind: "fixed", colliders: [{ layer: "ground", tag: "grass", shape: { type: "trimesh", vertices: pos, indices: Array.from({ length: pos.length / 3 }, (_, i) => i) } }] });
  const parts: THREE.BufferGeometry[] = [terrain];
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * Math.PI * 2 + rng() * 0.3;
    const r = 11 + rng() * 6;
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    parts.push(facet(new THREE.IcosahedronGeometry(0.4 + rng() * 0.5, 0).translate(x, targetsHeight(x, z) + 0.2, z), { colour: look.palette(pick(["stone.a", "stone.b"])), jitter: 0.12, tint: 0.08, rng }));
  }
  const mesh = new THREE.Mesh(merge(parts.map((p) => (p.index ? p.toNonIndexed() : p))), look.surface);
  mesh.name = "targets:world";
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}
